import { Card, Steps, Tag, Collapse, Timeline, Modal, Descriptions } from "antd";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  ClockCircleFilled,
  EnvironmentOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  UserOutlined,
} from "@ant-design/icons";
import SampleImage from "../../assests/sample.jpg";
import OverAll from "./OverAll";
import axios from "axios";
import dayjs from "dayjs";

const { Step } = Steps;
const { Panel } = Collapse;

export default function TodaysLog() {
  const { authData, backUrl } = useContext(AuthContext);
  const [showOverall, setShowOverall] = useState(false);
  const [showOnDuty, setShowOnDuty] = useState(false);
  const [TodayData, setTodayData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(false);
  const [onDutyRequests, setOnDutyRequests] = useState([]);
  const [showLead, setShowLead] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

  const today = dayjs().format("YYYY-MM-DD");

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${backUrl}/api/hr/attendance-comparsion/?date=${today}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setTodayData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setTodayData([]);
    }
  };

  const fetchOnduty = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backUrl}/api/hr/attendance/my-onduty-requests/`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setOnDutyRequests(response.data);
    } catch (err) {
      console.log(err.response?.data?.message || err.message);
      console.log("Failed to fetch onduty requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOnduty();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openImage = (url) => {
    if (url) window.open(url, "_blank");
  };

  const getAttendanceStatusColor = (status, remarks, leaveType) => {
    const s = (status || "").toUpperCase();
    const r = (remarks || "").toUpperCase();
    const sec = (leaveType || "").toUpperCase();

    // 1. HALF DAY VARIANTS
    if (r === "HALF DAY - SICK" || r === "HALF DAY - SICK LEAVE" || sec.includes("SICK")) {
        return "#B1A1C8";
    }
    if (r === "HALF DAY - CASUAL" || r === "HALF DAY - CASUAL LEAVE" || sec.includes("CASUAL")) {
        return "#FE66CB";
    }
    if (r === "HALF DAY - COMPOFF" || (sec.includes("COMP") && (s.includes("HALF") || r.includes("HALF")))) {
        return "#7c9150";
    }
    if (r === "HALF DAY - WEEKOFF" || (sec.includes("WEEK") && (s.includes("HALF") || r.includes("HALF")))) {
        return "#a068a3";
    }
    if (s.includes("HALF") || r.includes("HALF")) {
        return "#FE66CB";
    }

    // 2. COMP OFF (Taken Comp Off / Earned Comp Off / Leave with Compoff)
    if (s.includes("COMP") || r.includes("COMP") || sec.includes("COMP")) {
        return "#1FA2CC"; // Sky Blue Comp Off (matches Legend #1FA2CC)
    }

    // 3. WEEK OFF (Taken Week Off / Regular Week Off / Leave with Weekoff)
    if (s.includes("WEEK") || r.includes("WEEK") || s.includes("OFF DAY") || r.includes("OFF DAY") || s === "WO" || sec.includes("WEEK") || s === "SUNDAY") {
        return "#FFFF00"; // Pure Yellow Week Off (matches Legend #FFFF00)
    }

    // 4. SICK LEAVE
    if (sec.includes("SICK") || s.includes("SICK") || r.includes("SICK")) {
        return "#FF0066"; // Deep Pink Sick Leave
    }

    // 5. CASUAL LEAVE
    if (sec.includes("CASUAL") || s.includes("CASUAL") || r.includes("CASUAL")) {
        return "#CB00CC"; // Purple Casual Leave
    }

    const statusColorMap = {
      PRESENT: "#00AF50",                        // dark green
      ABSENT: "#FE0000",                         // red
      "GRACE - LATE IN": "#FFFECB",             // light mint green
      "GRACE – EARLY OUT": "#FFFECB",           // light mint green
      "LATE IN": "#FFFECB",                      // light yellow
      "EARLY OUT": "#FFFECB",                    // light yellow
      "SHORTFALL PRESENT": "#298988",            // dark teal
      "HALF DAY - SHORTFALL PRESENT": "#58BABB", // medium teal/cyan
      "SHORT HOURS": "#DED9C5",                  // beige/tan
      "MISPUNCH OUT": "#BFBFBF",                 // medium gray
      "MISPUNCH IN": "#BFBFBF",                  // medium gray
      PERMISSION: "#98FF66",                     // bright lime green
      HOLIDAY: "#F79647",                        // orange
      SUNDAY: "#FFFF00",                         // pure yellow
      COMPOFF: "#1FA2CC",                        // sky/cyan blue
      DEFAULT: "white",
      WEEKOFF: "#FFFF00",                        // pure yellow
      "WEEK OFF": "#FFFF00",
      "WEEK_OFF": "#FFFF00",
      WO: "#FFFF00",
      "OFF DAY": "#FFFF00",
      TECHNICAL_ISSUE: "#303343",
      CASUAL: "#CB00CC",                         // magenta/purple
      SICK: "#FF0066",                           // hot pink / deep pink
      "HALF DAY - CASUAL LEAVE": "#FE66CB",     // light pink
      "HALF DAY - SICK LEAVE": "#B1A1C8",
      "LOP": "#bf4406",
      "HALF DAY - COMPOFF": "#7c9150",
      "HALF DAY - WEEKOFF": "#a068a3",
    };
    return (
      statusColorMap[sec] ||
      statusColorMap[r] ||
      statusColorMap[s] ||
      "#d97706"
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "#16a34a";
      case "PENDING":
        return "#d97706";
      case "REJECTED":
        return "#dc2626";
      default:
        return "#888";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircleOutlined />;
      case "PENDING":
        return <SyncOutlined spin />;
      case "REJECTED":
        return <CloseCircleOutlined />;
      default:
        return <ClockCircleFilled />;
    }
  };

  const renderOnDutyRequests = () => {
    if (!showOnDuty || !onDutyRequests || onDutyRequests.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: 30, padding: isMobile ? "0 10px" : "0 40px" }}>
        <h3
          style={{
            fontSize: isMobile ? 18 : 20,
            fontWeight: 700,
            marginBottom: 20,
            color: "#0e497b",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FileTextOutlined />
          On-Duty Requests
        </h3>

        {onDutyRequests.map((request) => (
          <Card
            key={request.id}
            style={{
              marginBottom: 20,
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `2px solid ${getStatusColor(request.status)}`,
            }}
            bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
          >
            {/* Header Section */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                  {request.employee_name} ({request.employee_code})
                </div>
                <div style={{ marginBottom: 6 }}>
                  <p
                    style={{
                      fontSize: isMobile ? 12 : 13,
                      color:
                        request.status === "APPROVED" ? "success"
                          : request.status === "PENDING" ? "warning"
                            : "error",
                    }}
                  >
                    {request.status}
                  </p>
                </div>
                <p><strong>Onduty Type:</strong> {request.onduty_type.toUpperCase()}</p>
              </div>
            </div>

            {/* Details Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
                marginBottom: 16,
                backgroundColor: "#ffffff",
                padding: isMobile ? "12px" : "16px",
                borderRadius: 8,
                border: "1px solid #ffffff",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />Start Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.start_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />End Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.end_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />Location
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {request.location}
                </div>
              </div>
              {request.remarks && (
                <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Remarks</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#374151" }}>
                    "{request.remarks}"
                  </div>
                </div>
              )}
            </div>

            {/* Approval Logs Section */}
            <Collapse bordered={false} style={{ backgroundColor: "#fff", borderRadius: 8 }}>
              <Panel
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>
                    Approval Logs ({request.approval_logs?.filter((log) => log.status === "APPROVED").length || 0}/{request.approval_logs?.length || 0})
                  </div>
                }
                key="1"
              >
                <Timeline
                  style={{ marginTop: 16 }}
                  items={request.approval_logs?.map((log, index) => ({
                    color: getStatusColor(log.status),
                    children: (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: 8,
                            flexWrap: "wrap",
                            gap: 8,
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: "#333" }}>
                              {log.approver_name}
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                              Updated: {dayjs(log.updated_at).format("MMM DD, YYYY hh:mm A")}
                            </div>
                          </div>
                          <Tag
                            color={log.status === "APPROVED" ? "success" : log.status === "PENDING" ? "warning" : "error"}
                            style={{ fontSize: 12 }}
                          >
                            {log.status}
                          </Tag>
                        </div>
                        <div
                          style={{
                            backgroundColor: "#f9fafb",
                            padding: "8px 12px",
                            borderRadius: 6,
                            fontSize: 13,
                            color: "#555",
                            fontStyle: "italic",
                            marginTop: 8,
                          }}
                        >
                          <strong>Remarks: </strong>{log.remarks ? log.remarks : "No remarks found"}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </Panel>
            </Collapse>
          </Card>
        ))}
      </div>
    );
  };

  return showOverall ? (
    <OverAll onBack={() => setShowOverall(false)} />
  ) : (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: isMobile ? "center" : "flex-start",
          gap: 12,
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setShowOverall(true)}
          style={{
            marginBottom: 20,
            padding: isMobile ? "8px 14px" : "6px 16px",
            backgroundColor: "#0e497b",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: isMobile ? 13 : 14,
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: isMobile ? "45%" : "auto",
          }}
        >
          <BarChartOutlined style={{ fontSize: isMobile ? 14 : 16 }} />
          Overall
        </button>
      </div>

      {!TodayData || TodayData.length === 0 ? (
        <p style={{ textAlign: "center", fontSize: isMobile ? 14 : 16, color: "#888", marginTop: 30 }}>
          No attendance record found for today.
        </p>
      ) : (
        <>
          {TodayData[0]?.FirstInTime !== null ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                width: "100%",
                marginTop: 20,
                padding: isMobile ? "0 10px" : "0 40px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 600,
                  background: "#fff",
                  borderRadius: 12,
                  padding: isMobile ? "10px 12px" : "20px 25px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Steps direction="vertical" current={TodayData.length} style={{ width: "100%" }}>
                  {TodayData.map((item, index) => {
                    const punchDetails = item?.punch_details || [];

                    return (
                      <Step
                        key={index}
                        title={
                          <div
                            style={{
                              fontSize: isMobile ? 15 : 16,
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                color: getAttendanceStatusColor(item.Status, item.AttendanceStatus),
                                fontSize: isMobile ? 15 : 16,
                              }}
                            >
                              {/* Title stays empty like CircularOverAll — work site shown per punch in description */}
                            </span>
                          </div>
                        }
                        description={
                          <div
                            style={{
                              marginTop: 10,
                              fontSize: isMobile ? 14 : 15,
                              lineHeight: 2,
                              wordBreak: "break-word",
                            }}
                          >
                            {punchDetails.map((punch, punchIdx) => (
                              <div key={punchIdx} style={{ marginBottom: punchIdx < punchDetails.length - 1 ? 12 : 0 }}>

                                {/* Work site label — only for non-biometric */}
                                {punch?.RecordType !== "BIOMETRIC" && (
                                  <span
                                    style={{
                                      color: getAttendanceStatusColor(item.Status, item.AttendanceStatus),
                                      fontSize: isMobile ? 15 : 16,
                                      fontWeight: 600,
                                      display: "block",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {[
                                      punch.check_in_work_site && `${punch.check_in_work_site} (IN)`,
                                      punch.check_out_work_site && `${punch.check_out_work_site} (OUT)`,
                                    ]
                                      .filter(Boolean)
                                      .join(" - ")}
                                  </span>
                                )}

                                {/* Punch In / Punch Out */}
                                {punch.PunchIn || punch.PunchOut ? (
                                  <>
                                    <div>
                                      <strong>Punch In:</strong>{" "}
                                      {punch.PunchIn ?? "No Data"}
                                    </div>
                                    <div>
                                      <strong>Punch Out:</strong>{" "}
                                      {punch.PunchOut ?? "No Data"}
                                    </div>
                                  </>
                                ) : null}

                                {/* Check In / Check Out */}
                                {punch.CheckIn || punch.CheckOut ? (
                                  <>
                                    <div className="d-flex">
                                      <div>
                                        <strong>Check In:</strong>{" "}
                                        {punch.CheckIn ?? "No Data"}
                                      </div>
                                      {punch.CheckIn && (
                                        <div className="ms-2">
                                          <img
                                            src={punch.check_in_photo_url || SampleImage}
                                            alt="Check-in"
                                            onClick={() => openImage(punch.check_in_photo_url || SampleImage)}
                                            style={{
                                              width: isMobile ? 40 : 50,
                                              height: isMobile ? 40 : 50,
                                              borderRadius: "50%",
                                              objectFit: "cover",
                                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                              cursor: "pointer",
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>

                                    <div className="d-flex">
                                      <div>
                                        <strong>Check Out:</strong>{" "}
                                        {punch.CheckOut ?? "No Data"}
                                      </div>
                                      {punch.CheckOut && (
                                        <div className="ms-2">
                                          <img
                                            src={punch.check_out_photo_url || SampleImage}
                                            alt="Check-out"
                                            onClick={() => openImage(punch.check_out_photo_url || SampleImage)}
                                            style={{
                                              width: isMobile ? 40 : 50,
                                              height: isMobile ? 40 : 50,
                                              borderRadius: "50%",
                                              objectFit: "cover",
                                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                              cursor: "pointer",
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : null}

                                {(() => {
                                  const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                                  if (!raw) return false;
                                  try {
                                    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                                    if (Array.isArray(parsed)) return parsed.length > 0;
                                    if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                                    return !!parsed;
                                  } catch (e) {
                                    return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                                  }
                                })() && (
                                  <div style={{ marginTop: 8 }}>
                                    <a
                                      onClick={() => {
                                        const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                                        let parsed = raw;
                                        if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { } }
                                        const arr = Array.isArray(parsed) ? parsed : [parsed];
                                        setSelectedLeadDetails(arr);
                                        setShowLead(true);
                                      }}
                                      style={{ color: "#0e497b", cursor: "pointer", fontSize: "13px", fontWeight: 600, textDecoration: "underline" }}
                                    >
                                      Visit Lead Details
                                    </a>
                                  </div>
                                )}

                                {/* Dashed divider between multiple punch records */}
                                {punchIdx < punchDetails.length - 1 && (
                                  <hr style={{ borderColor: "#e5e7eb", margin: "10px 0", borderStyle: "dashed" }} />
                                )}
                              </div>
                            ))}

                            <hr style={{ borderColor: "#eee", margin: "12px 0" }} />
                          </div>
                        }
                        icon={
                          <ClockCircleFilled
                            style={{
                              color: getAttendanceStatusColor(item.Status, item.AttendanceStatus),
                              fontSize: 18,
                            }}
                          />
                        }
                      />
                    );
                  })}
                </Steps>
              </div>
            </div>
          ) : (
            <p style={{ textAlign: "center", fontSize: isMobile ? 14 : 16, color: "#888", marginTop: 30 }}>
              No attendance record found for today.
            </p>
          )}
        </>
      )}

      {/* On-Duty Requests Section */}
      {renderOnDutyRequests()}

      <Modal
        open={showLead}
        footer={null}
        onCancel={() => {
          setShowLead(false);
          setSelectedLeadDetails(null);
        }}
        width={600}
      >
        <h3 style={{ marginBottom: 20 }}>Lead Details</h3>
        {selectedLeadDetails && selectedLeadDetails.map((lead, index) => (
          <Descriptions
            key={index}
            bordered
            column={1}
            size="small"
            style={{ marginBottom: 16 }}
            title={selectedLeadDetails.length > 1 ? `Lead ${index + 1}` : null}
          >
            <Descriptions.Item label="Lead No"><strong>{lead.lead_no || lead.lead_id || "-"}</strong></Descriptions.Item>
            <Descriptions.Item label="Customer">{lead.customer_name || lead.name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Email">{lead.email || lead.customer_email || "-"}</Descriptions.Item>
            <Descriptions.Item label="Phone">{lead.number || lead.phone || "-"}</Descriptions.Item>
            <Descriptions.Item label="Project">{lead.project_name || lead.project_id || (lead.project && lead.project.title) || "-"}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <span style={{
                padding: "2px 8px", borderRadius: "4px", backgroundColor: "#f6ffed", color: "#52c41a", fontWeight: 500, border: "1px solid #b7eb8f"
              }}>
                {lead.status?.title || lead.status || "-"}
              </span>
            </Descriptions.Item>
          </Descriptions>
        ))}
      </Modal>
    </>
  );
}